import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';

import { Page, snackbar } from '@occc-app/design-system';
import {
  useArchiveExternalLoyaltyManualOfferMutation,
  useCreateExternalLoyaltyManualOfferInActivateMutation,
} from '@occc/graphql';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';

import {
  PAGE_TITLE,
  PAGE_SUBTITLE,
  ACTIVE_TABLE_TITLE,
  INACTIVE_TABLE_TITLE,
  UPCOMING_TABLE_TITLE,
} from '../../tobacco-api-offers.constants';
import { routeCreator } from '../../tobacco-api-offers.router';
import { useOffersFilter } from '../../hooks/use-offers-filter.hook';
import { useTobaccoApiOffersPagination } from '../../hooks/use-tobacco-api-offers-pagination.hook';
import { usePendingOfferPolling } from '../../hooks/use-pending-offer-polling.hook';

import { OffersTable } from './components/offers-table.component';

export const TobaccoApiOffersPage = () => {
  const navigate = useNavigate();
  const isPermittedToEdit = useIsUserPermittedTo(Feature.TOBACCO_API_OFFERS_EDIT);
  const [actionLoadingUuid, setActionLoadingUuid] = useState<string | undefined>();
  const { isListPollingEnabled, pendingOfferUuid, startPolling } = usePendingOfferPolling();

  const [createInActivate] = useCreateExternalLoyaltyManualOfferInActivateMutation({
    refetchQueries: ['getExternalLoyaltyManualOffers'],
  });

  const [archiveOffer] = useArchiveExternalLoyaltyManualOfferMutation({
    refetchQueries: ['getExternalLoyaltyManualOffers'],
  });

  const activeFilter = useOffersFilter('active');
  const inactiveFilter = useOffersFilter('inactive');
  const upcomingFilter = useOffersFilter('upcoming');

  const activeOffers = useTobaccoApiOffersPagination({
    tableType: 'active',
    userFilter: activeFilter.filterInput,
    isPollingEnabled: isListPollingEnabled,
  });

  const inactiveOffers = useTobaccoApiOffersPagination({
    tableType: 'inactive',
    userFilter: inactiveFilter.filterInput,
    isPollingEnabled: isListPollingEnabled,
  });

  const upcomingOffers = useTobaccoApiOffersPagination({
    tableType: 'upcoming',
    userFilter: upcomingFilter.filterInput,
    isPollingEnabled: isListPollingEnabled,
  });

  const handleOfferClick = useCallback(
    (uuid: string) => {
      navigate(routeCreator.details(uuid));
    },
    [navigate],
  );

  const handleCreateOffer = useCallback(
    async (uuid: string, offerName: string) => {
      setActionLoadingUuid(uuid);
      try {
        await createInActivate({ variables: { uuid, offerName } });
        snackbar.success('Offer successfully created in Activate.');
        startPolling(uuid, 'create');
      } catch {
        snackbar.error('Something went wrong. Please try again.');
      } finally {
        setActionLoadingUuid(undefined);
      }
    },
    [createInActivate, startPolling],
  );

  const handleDeleteOffer = useCallback(
    async (uuid: string) => {
      setActionLoadingUuid(uuid);
      try {
        await archiveOffer({ variables: { uuid } });
        snackbar.success('Offer successfully deleted.');
        startPolling(uuid, 'delete');
      } catch {
        snackbar.error('Something went wrong. Please try again.');
      } finally {
        setActionLoadingUuid(undefined);
      }
    },
    [archiveOffer, startPolling],
  );

  const offerActionLockedUuid = actionLoadingUuid ?? (isListPollingEnabled ? pendingOfferUuid ?? undefined : undefined);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        subtitle: PAGE_SUBTITLE,
      }}
    >
      <Stack spacing={3}>
        <OffersTable
          title={ACTIVE_TABLE_TITLE}
          data={activeOffers.data}
          loading={activeOffers.loading}
          error={activeOffers.error}
          tableType='active'
          paginationProps={activeOffers.paginationProps}
          filterValues={activeFilter.filterValues}
          isFiltered={activeFilter.isFiltered}
          onFilterApply={activeFilter.onFilterApply}
          onFilterReset={activeFilter.onFilterReset}
          onOfferClick={handleOfferClick}
          onOfferCreate={isPermittedToEdit ? handleCreateOffer : undefined}
          onOfferDelete={isPermittedToEdit ? handleDeleteOffer : undefined}
          actionLoadingUuid={offerActionLockedUuid}
        />

        <OffersTable
          title={INACTIVE_TABLE_TITLE}
          data={inactiveOffers.data}
          loading={inactiveOffers.loading}
          error={inactiveOffers.error}
          tableType='inactive'
          paginationProps={inactiveOffers.paginationProps}
          filterValues={inactiveFilter.filterValues}
          isFiltered={inactiveFilter.isFiltered}
          onFilterApply={inactiveFilter.onFilterApply}
          onFilterReset={inactiveFilter.onFilterReset}
          onOfferClick={handleOfferClick}
          onOfferCreate={isPermittedToEdit ? handleCreateOffer : undefined}
          onOfferDelete={isPermittedToEdit ? handleDeleteOffer : undefined}
          actionLoadingUuid={offerActionLockedUuid}
        />

        <OffersTable
          title={UPCOMING_TABLE_TITLE}
          data={upcomingOffers.data}
          loading={upcomingOffers.loading}
          error={upcomingOffers.error}
          tableType='upcoming'
          paginationProps={upcomingOffers.paginationProps}
          filterValues={upcomingFilter.filterValues}
          isFiltered={upcomingFilter.isFiltered}
          onFilterApply={upcomingFilter.onFilterApply}
          onFilterReset={upcomingFilter.onFilterReset}
          onOfferClick={handleOfferClick}
          onOfferCreate={isPermittedToEdit ? handleCreateOffer : undefined}
          onOfferDelete={isPermittedToEdit ? handleDeleteOffer : undefined}
          actionLoadingUuid={offerActionLockedUuid}
        />
      </Stack>
    </Page>
  );
};
